import { Locale } from 'enums/locale';
import {
  HelmetSettings,
  RankMathResponse,
  VehicleProductSchemaData,
  WordpressPageResponse,
} from 'types/seo.type';
import { seoClient } from 'utils/axios';
import he from 'he';
import { getLocalePrefix } from 'utils/getLocalePrefix';

export async function getSeoData(asPath: string, locale: Locale, url: string) {
  const { pageData, seoData } = await getDataFromWordpress(asPath, locale);

  let content: string | null = null;
  const helmetSettings: HelmetSettings = {
    title: '',
    meta: [],
    script: '',
  };

  if (seoData.status === 'fulfilled') {
    const { title, meta } = generateMetaAndScriptFromRankMath(
      seoData.value.data,
      url
    );
    helmetSettings.meta = meta;
    helmetSettings.title = getTitleFromPageTitle(title);
  }

  if (pageData.status === 'fulfilled') {
    const {
      value: {
        data: [page],
      },
    } = pageData;

    if (!helmetSettings.title) {
      helmetSettings.title = getTitleFromPageTitle(page?.title.rendered);
    }

    content = page?.content.rendered ?? null;
  }

  return {
    helmetSettings,
    content,
  };
}

const getDataFromWordpress = async (asPath: string, locale: Locale) => {
  const localePrefix = locale === Locale.TR ? '' : '/en';
  const pageBlogUrl = `${process.env.NEXT_PUBLIC_BLOG_BASEPATH}${localePrefix}${asPath}`;

  const seoPromise = seoClient.get<RankMathResponse>(
    `${getLocalePrefix(locale)}wp-json/rankmath/v1/getHead`,
    {
      params: {
        url: pageBlogUrl,
      },
    }
  );
  const pagePromise = seoClient.get<WordpressPageResponse[]>(
    `${getLocalePrefix(locale)}wp-json/wp/v2/pages/?slug=${asPath}`,
    {
      // params: {
      //   slug: `${asPath.substring(1)}`,
      // },
    }
  );

  const [seoData, pageData] = await Promise.allSettled([
    seoPromise,
    pagePromise,
  ]);

  return {
    seoData,
    pageData,
  };
};

export const getTitleFromPageTitle = (pageTitle: string | undefined) => {
  if (!pageTitle) {
    return 'Teknevia | Türkiyenin en yenilikçi yat & tekne kiralama platformu';
  }
  const titleStripped = pageTitle
    .replace(/<\/?[^>]+(>|$)/g, '')
    .replace('- Teknevia', '| Teknevia');
  if (titleStripped.toLowerCase().includes('| teknevia')) {
    return he.decode(titleStripped);
  }
  return he.decode(`${titleStripped}`);
};

const getOgShareMetas = ({
  pageTitle,
  description,
  url,
  locale,
  image,
  isNoIndex = false,
}: {
  pageTitle: string;
  description?: string;
  url: string;
  locale: Locale;
  image?: string;
  isNoIndex?: boolean;
}): Record<string, string>[] => {
  return [
    ...(!isNoIndex
      ? [
          {
            name: 'robots',
            content:
              'follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large',
          },
        ]
      : [{ name: 'robots', content: 'noindex' }]),
    {
      property: 'og:locale',
      content: locale,
    },
    {
      property: 'og:type',
      content: 'article',
    },
    {
      property: 'og:title',
      content: pageTitle,
    },
    {
      property: 'og:image',
      content: image ?? 'https://cdn.teknevia.com/storefront/common/logo.png',
    },
    ...(description
      ? [
          {
            property: 'og:description',
            content: description,
          },
        ]
      : []),
    {
      property: 'og:url',
      content: url,
    },
    {
      property: 'og:site_name',
      content: 'Teknevia',
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'twitter:title',
      content: pageTitle,
    },
    ...(description
      ? [
          {
            name: 'twitter:description',
            content: description,
          },
        ]
      : []),
    {
      name: 'twitter:image',
      content: image ?? 'https://cdn.teknevia.com/storefront/common/logo.png',
    },
    {
      name: 'twitter:site',
      content: '@TekneVia',
    },
    {
      name: 'twitter:creator',
      content: '@TekneVia',
    },
  ];
};

const replaceMetaImages = (meta: NonNullable<HelmetSettings['meta']>) => {
  const metaCopy = [...meta];
  if (metaCopy) {
    const ogImage = metaCopy.find((m) => m.property === 'og:image');
    if (!ogImage) {
      metaCopy.push({
        property: 'og:image',
        content: 'https://cdn.teknevia.com/storefront/common/logo.png',
      });
      metaCopy.push({
        property: 'twitter:image',
        content: 'https://cdn.teknevia.com/storefront/common/logo.png',
      });
    }
  }
  return metaCopy;
};

const replaceSiteName = (meta: NonNullable<HelmetSettings['meta']>) => {
  const metaCopy = [...meta];
  const ogSiteNameIndex = metaCopy.findIndex(
    (m) => m.property === 'og:site_name'
  );
  if (ogSiteNameIndex > -1) {
    metaCopy[ogSiteNameIndex].content = 'Teknevia';
  }
  return metaCopy;
};

const generateMetaAndScriptFromRankMath = (
  seoData: RankMathResponse,
  url: string
) => {
  const { head } = seoData;

  let meta: NonNullable<HelmetSettings['meta']> = [];
  let title = '';

  const mainDomainHead = head.replaceAll(
    'https://blog.teknevia.com',
    process.env.NEXT_PUBLIC_HOST
  );

  const splittedTags: string[] = mainDomainHead
    .split('\n')
    .filter((h: string) => !!h);
  const attrRegexp = /(name|content|property)="([^"]*)"/g;

  splittedTags.forEach((tag) => {
    if (tag.startsWith('<meta')) {
      const metaObj: Record<string, string> = {};
      const attrMatchedTags = [...tag.matchAll(attrRegexp)];
      attrMatchedTags.forEach((match, idx) => {
        if (idx > 0 && attrMatchedTags[0][2] === 'og:title') {
          title = match[2];
        }
        if (checkRobotsNoFollowOrNoIndex(attrMatchedTags)) {
          metaObj.name = 'robots';
          metaObj.content =
            'follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large';
          return;
        }
        metaObj[match[1]] = match[2];
        if (idx > 0 && attrMatchedTags[0][2].includes('url')) {
          metaObj[match[1]] = `https://www.teknevia.com${url}`;
        }
      });
      if (Object.keys(metaObj).length > 0) {
        meta.push(metaObj);
      }
    }
  });

  meta = replaceMetaImages(meta);
  meta = replaceSiteName(meta);

  return { meta, title };
};

const checkRobotsNoFollowOrNoIndex = (attrMatchedTags: RegExpMatchArray[]) => {
  const tag = attrMatchedTags[0][2];
  const content = attrMatchedTags[1][2];
  return (
    (tag === 'robots' && content.includes('nofollow')) ||
    content.includes('noindex')
  );
};

export const getMetadataForCustomPages = (
  title: string,
  url: string,
  locale: Locale,
  description?: string
) => {
  const pageTitle = getTitleFromPageTitle(title);
  const metadata: Partial<HelmetSettings> = {
    title: pageTitle,
  };

  metadata.meta = [...getOgShareMetas({ pageTitle, url, locale })];

  if (description) {
    const summaryDescription =
      description.length > 160
        ? description.substring(0, 160).split(/ |\n/).slice(0, -1).join(' ') +
          '...'
        : description;
    metadata.meta.push({
      name: 'description',
      content: he.decode(summaryDescription),
    });
  }

  return metadata;
};

export const getMetadataForGuardedPages = (
  title: string,
  url: string,
  locale: Locale
) => {
  const metadata = getMetadataForCustomPages(title, url, locale);

  if (!metadata.meta) {
    metadata.meta = [];
  }

  metadata.meta.push({
    name: 'robots',
    content: 'noindex',
  });
  metadata.meta.push({
    name: 'googlebot',
    content: 'noindex',
  });

  return metadata;
};

export const getSearchMetadata = (
  locale: Locale,
  url: string,
  title: string,
  description: string,
  noIndex?: boolean
): Partial<HelmetSettings> => {
  const helmetSettings: Partial<HelmetSettings> = {
    title: getTitleFromPageTitle(title),
    meta: [
      ...getOgShareMetas({
        pageTitle: getTitleFromPageTitle(title),
        url,
        locale,
        description,
        isNoIndex: noIndex,
      }),
      {
        name: 'description',
        content: description,
      },
    ],
  };

  return helmetSettings;
};

const getVehicleScriptSchema = ({
  description,
  rating,
  commentCount,
  ratingCount,
  images,
  name,
  url,
  category,
}: VehicleProductSchemaData) => {
  // -category (Kiralık Tekneler vs gibi kw kullanımlı)
  const aggregateRating = `,"aggregateRating": {"@type": "AggregateRating","ratingValue": "${
    rating || 4
  }","reviewCount": "${commentCount || 1}","ratingCount": "${
    ratingCount || 1
  }"}`;
  return `{"@context": "https://www.schema.org","@type": "Product","url": "${url}","name": "${name}","description": "${description}","image": [${images
    .map((i) => `"${i}"`)
    .join(',')}],"category": "${category}"${aggregateRating}}`;
};

export const getVehicleHelmetSettings = (
  title: string,
  description: string,
  locale: Locale,
  url: string,
  vehicleImage: string,
  vehicleProductSchemaData: VehicleProductSchemaData
): HelmetSettings => {
  const pageTitle = getTitleFromPageTitle(title);
  return {
    title: pageTitle,
    meta: [
      {
        name: 'description',
        content: description,
      },
      ...getOgShareMetas({
        pageTitle,
        description,
        image: vehicleImage,
        url,
        locale,
      }),
    ],
    script: getVehicleScriptSchema(vehicleProductSchemaData),
  };
};

export const getPartnerTitleDescription = (
  description: string,
  locale: Locale
) => {
  const title =
    locale === Locale.TR
      ? "Teknevia'nın Partneri Olun!"
      : "Become Teknevia's Partner!";

  const pageTitle = `${title}`;
  const url = locale === Locale.TR ? 'partner' : '/en/partner';
  return {
    title: pageTitle,
    meta: [
      {
        name: 'description',
        content: description,
      },
      ...getOgShareMetas({
        locale,
        pageTitle,
        url,
        description,
        image: 'https://cdn.teknevia.com/storefront/partner/partner.png',
      }),
    ],
  } as HelmetSettings;
};
